'use client';

import * as Dialog from "@radix-ui/react-dialog";
import { useState, useRef, useCallback, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUpload,
  faSpinner,
  faTimes,
  faCheck,
  faMagicWandSparkles,
  faCircleCheck,
  faGear,
  faRotateLeft,
  faEye,
  faEyeSlash
} from "@fortawesome/free-solid-svg-icons";
import {
  faInstagram,
  faFacebook,
  faWhatsapp
} from "@fortawesome/free-brands-svg-icons";
import { useUser } from "@/context/UserContext";
import { showToast } from "@/components/ui/Toast";
import { removeBackgroundImage } from "@/services/backgroundRemoval.service";
import { resolveMediaUrl } from "@/lib/media";
import { LogoConfirmationPopup } from "./LogoConfirmationPopup";
import { useEditorPreferences, FieldVisibility, DEFAULT_FIELD_VISIBILITY } from "@/hooks/useEditorPreferences";

interface BrandOnboardingModalProps {
  readonly isOpen: boolean;
  readonly onComplete: () => void;
  readonly onDismiss: () => void;
  /** Skip welcome screen and go directly to configuration */
  readonly skipWelcome?: boolean;
}

type OnboardingStep = 'welcome' | 'configure';

interface FormData {
  logoFile: File | null;
  logoPreview: string | null;
  primaryColor: string;
  instagram: string;
  facebook: string;
  whatsapp: string;
  website: string;
  address: string;
}

export function BrandOnboardingModal({
  isOpen,
  onComplete,
  onDismiss,
  skipWelcome = false
}: BrandOnboardingModalProps) {
  const { user, updateLogo, updateContact, updatePrimaryColor } = useUser();
  
  const [step, setStep] = useState<OnboardingStep>('welcome');
  const [isAnimating, setIsAnimating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Logo confirmation popup state
  const [showLogoPopup, setShowLogoPopup] = useState(false);
  const [isRemovingBackground, setIsRemovingBackground] = useState(false);
  const [processedLogoUrl, setProcessedLogoUrl] = useState<string | null>(null);
  
  // Store previous logo state to restore on cancel
  const [previousLogoState, setPreviousLogoState] = useState<{
    logoFile: File | null;
    logoPreview: string | null;
  } | null>(null);
  
  const [formData, setFormData] = useState<FormData>({
    logoFile: null,
    logoPreview: null,
    primaryColor: '#A020F0',
    instagram: '',
    facebook: '',
    whatsapp: '',
    website: '',
    address: ''
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load existing user data when modal opens
  useEffect(() => {
    if (isOpen && user?.profile) {
      const profile = user.profile;
      // Resolve logo URL if it exists
      const resolvedLogoUrl = profile.logoUrl ? resolveMediaUrl(profile.logoUrl) : null;
      setFormData(prev => ({
        ...prev,
        logoPreview: resolvedLogoUrl || null,
        primaryColor: profile.primaryColor || '#A020F0',
        instagram: profile.instagram || '',
        facebook: profile.facebook || '',
        whatsapp: profile.whatsapp || '',
        website: profile.website || '',
        address: profile.address || ''
      }));
      // Reset processed logo when opening
      setProcessedLogoUrl(null);
    }
  }, [isOpen, user?.profile]);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      setStep(skipWelcome ? 'configure' : 'welcome');
    }
  }, [isOpen, skipWelcome]);

  const handleLogoSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showToast('Por favor, selecione uma imagem válida', 'error');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      showToast('A imagem deve ter no máximo 10MB', 'error');
      return;
    }

    // Save previous state before changing
    setPreviousLogoState({
      logoFile: formData.logoFile,
      logoPreview: formData.logoPreview
    });

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setFormData(prev => ({
        ...prev,
        logoFile: file,
        logoPreview: e.target?.result as string
      }));
      setProcessedLogoUrl(null);
      setShowLogoPopup(true);
    };
    reader.readAsDataURL(file);
  }, []);

  const handleRemoveBackground = useCallback(async () => {
    if (!formData.logoPreview || !formData.logoFile) return;

    setIsRemovingBackground(true);
    try {
      const result = await removeBackgroundImage(
        formData.logoPreview,
        formData.logoFile.name
      );
      
      // Use the processed image URL
      const processedUrl = result.url_hd || result.url_preview;
      setProcessedLogoUrl(processedUrl);
      showToast('Fundo removido com sucesso!', 'success');
    } catch (error) {
      console.error('Error removing background:', error);
      showToast('Erro ao remover fundo. Tente novamente.', 'error');
    } finally {
      setIsRemovingBackground(false);
    }
  }, [formData.logoPreview, formData.logoFile]);

  const handleSaveLogo = useCallback(() => {
    setShowLogoPopup(false);
    // Clear previous state since we're saving
    setPreviousLogoState(null);
    // If background was removed, update the preview with processed URL
    if (processedLogoUrl) {
      setFormData(prev => ({
        ...prev,
        logoPreview: processedLogoUrl
      }));
    }
  }, [processedLogoUrl]);

  const handleCancelLogo = useCallback(() => {
    setShowLogoPopup(false);
    // Restore previous logo state
    if (previousLogoState) {
      setFormData(prev => ({
        ...prev,
        logoFile: previousLogoState.logoFile,
        logoPreview: previousLogoState.logoPreview
      }));
      setPreviousLogoState(null);
    }
    setProcessedLogoUrl(null);
  }, [previousLogoState]);

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    
    try {
      // Upload logo if selected
      if (formData.logoFile) {
        // If we have a processed logo URL (background removed), we need to fetch it as a file
        if (processedLogoUrl) {
          try {
            const response = await fetch(processedLogoUrl);
            const blob = await response.blob();
            const processedFile = new File([blob], formData.logoFile.name, { type: blob.type });
            await updateLogo(processedFile, setUploadProgress);
          } catch {
            // Fallback to original file if fetch fails
            await updateLogo(formData.logoFile, setUploadProgress);
          }
        } else {
          await updateLogo(formData.logoFile, setUploadProgress);
        }
      }

      // Update primary color
      if (formData.primaryColor) {
        await updatePrimaryColor(formData.primaryColor);
      }

      // Update contact info
      const contactPayload: Record<string, string> = {};
      if (formData.instagram) contactPayload.instagram = formData.instagram;
      if (formData.facebook) contactPayload.facebook = formData.facebook;
      if (formData.whatsapp) contactPayload.whatsapp = formData.whatsapp;
      if (formData.website) contactPayload.website = formData.website;
      if (formData.address) contactPayload.address = formData.address;

      if (Object.keys(contactPayload).length > 0) {
        await updateContact(contactPayload);
      }

      showToast('Marca configurada com sucesso!', 'success');
      onComplete();
    } catch (error) {
      console.error('Error saving brand config:', error);
      showToast('Erro ao salvar configurações. Tente novamente.', 'error');
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  }, [formData, processedLogoUrl, updateLogo, updatePrimaryColor, updateContact, onComplete]);

  const handleStartConfiguration = useCallback(() => {
    setStep('configure');
  }, []);

  // Editor preferences hook
  const { preferences, fieldVisibility, updatePreferences, resetPreferences, isSaving: isSavingPrefs } = useEditorPreferences();

  // Toggle visibility for a specific field
  const handleToggleFieldVisibility = useCallback(async (field: keyof FieldVisibility) => {
    const newVisibility = {
      ...fieldVisibility,
      [field]: !fieldVisibility[field]
    };
    await updatePreferences({ fieldVisibility: newVisibility });
  }, [fieldVisibility, updatePreferences]);

  return (
    <>
      <Dialog.Root 
        open={isOpen} 
        onOpenChange={(open) => {
          // Don't close if the logo popup is open
          if (!open && !showLogoPopup) {
            onDismiss();
          }
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-[1300] bg-black/70 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
          <Dialog.Content
            className="fixed left-1/2 top-1/2 z-[1400] w-[95vw] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-3xl border border-slate-700/50 bg-slate-900 p-6 shadow-2xl focus:outline-none"
            style={{
              animation: isAnimating ? 'bounceUp 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)' : 'none'
            }}
            onPointerDownOutside={(e) => {
              // Prevent closing when logo popup is open
              if (showLogoPopup) {
                e.preventDefault();
              }
            }}
            onInteractOutside={(e) => {
              // Prevent closing when logo popup is open
              if (showLogoPopup) {
                e.preventDefault();
              }
            }}
          >
            <style>{`
              @keyframes bounceUp {
                0% {
                  opacity: 0;
                  transform: translate(-50%, -50%) translateY(100px) scale(0.9);
                }
                50% {
                  transform: translate(-50%, -50%) translateY(-10px) scale(1.02);
                }
                100% {
                  opacity: 1;
                  transform: translate(-50%, -50%) translateY(0) scale(1);
                }
              }
            `}</style>

            {step === 'welcome' ? (
              <WelcomeStep
                onConfigure={handleStartConfiguration}
                onDismiss={onDismiss}
              />
            ) : (
              <ConfigureStep
                formData={formData}
                setFormData={setFormData}
                fileInputRef={fileInputRef}
                onLogoSelect={handleLogoSelect}
                onSubmit={handleSubmit}
                onDismiss={onDismiss}
                isSubmitting={isSubmitting}
                uploadProgress={uploadProgress}
                preferences={preferences}
                fieldVisibility={fieldVisibility}
                onToggleFieldVisibility={handleToggleFieldVisibility}
                onUpdatePreferences={updatePreferences}
                onResetPreferences={resetPreferences}
                isSavingPrefs={isSavingPrefs}
              />
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Logo Confirmation Popup */}
      <LogoConfirmationPopup
        isOpen={showLogoPopup}
        logoPreview={processedLogoUrl || formData.logoPreview}
        isRemovingBackground={isRemovingBackground}
        hasProcessedLogo={!!processedLogoUrl}
        onRemoveBackground={handleRemoveBackground}
        onSave={handleSaveLogo}
        onClose={handleCancelLogo}
      />
    </>
  );
}

// Welcome Step Component
interface WelcomeStepProps {
  onConfigure: () => void;
  onDismiss: () => void;
}

function WelcomeStep({ onConfigure, onDismiss }: WelcomeStepProps) {
  return (
    <div className="text-center">
      {/* Icon */}
      <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-emerald-400">
        <FontAwesomeIcon icon={faMagicWandSparkles} className="h-10 w-10 text-white" />
      </div>

      <Dialog.Title className="text-2xl font-bold text-white">
        Bem-vindo ao Artes Turbo! 🎨
      </Dialog.Title>
      
      <Dialog.Description className="mt-3 text-base text-slate-400">
        Para personalizar seus designs automaticamente, configure sua marca agora!
        Adicione seu logo, cores e informações de contato.
      </Dialog.Description>

      {/* Features list */}
      <div className="mt-6 space-y-3 text-left">
        <FeatureItem text="Logo aplicado automaticamente em todos os designs" />
        <FeatureItem text="Cores da sua marca em destaque" />
        <FeatureItem text="Informações de contato sempre atualizadas" />
      </div>

      {/* Buttons */}
      <div className="mt-8 flex flex-col gap-3">
        <button
          type="button"
          onClick={onConfigure}
          className="w-full rounded-full bg-gradient-to-r from-purple-600 to-emerald-500 px-6 py-3.5 text-base font-semibold text-white shadow-lg transition hover:shadow-xl hover:brightness-110"
        >
          Configurar minha marca
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="w-full rounded-full border border-slate-600 bg-transparent px-6 py-3 text-base font-medium text-slate-400 transition hover:bg-slate-800 hover:text-white"
        >
          Não, depois
        </button>
      </div>
    </div>
  );
}

function FeatureItem({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-emerald-500/20">
        <FontAwesomeIcon icon={faCircleCheck} className="h-4 w-4 text-emerald-400" />
      </div>
      <span className="text-sm text-slate-300">{text}</span>
    </div>
  );
}

// Configure Step Component
interface ConfigureStepProps {
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onLogoSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: () => void;
  onDismiss: () => void;
  isSubmitting: boolean;
  uploadProgress: number;
  preferences: {
    logo: boolean;
    color: boolean;
    instagram: boolean;
    facebook: boolean;
    whatsapp: boolean;
    website: boolean;
    address: boolean;
    skipPopup: boolean;
  };
  fieldVisibility: FieldVisibility;
  onToggleFieldVisibility: (field: keyof FieldVisibility) => Promise<void>;
  onUpdatePreferences: (prefs: Partial<ConfigureStepProps['preferences']>) => Promise<boolean>;
  onResetPreferences: () => Promise<boolean>;
  isSavingPrefs: boolean;
}

function ConfigureStep({
  formData,
  setFormData,
  fileInputRef,
  onLogoSelect,
  onSubmit,
  onDismiss,
  isSubmitting,
  uploadProgress,
  preferences,
  fieldVisibility,
  onToggleFieldVisibility,
  onUpdatePreferences,
  onResetPreferences,
  isSavingPrefs
}: ConfigureStepProps) {
  return (
    <div className="max-h-[80vh] overflow-y-auto">
      <Dialog.Title className="text-xl font-bold text-white">
        Configure sua Marca
      </Dialog.Title>
      <Dialog.Description className="mt-1 text-sm text-slate-400">
        Preencha as informações abaixo para personalizar seus designs
      </Dialog.Description>

      <div className="mt-6 space-y-5">
        {/* Logo Upload */}
        <div>
          <label className="mb-2 block text-sm font-semibold text-slate-300">
            Logo da sua marca
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={onLogoSelect}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex w-full items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-slate-600 bg-slate-800/50 p-6 transition hover:border-purple-500 hover:bg-slate-800"
          >
            {formData.logoPreview ? (
              <div className="flex flex-col items-center gap-3">
                <img
                  src={formData.logoPreview}
                  alt="Logo preview"
                  className="h-20 w-20 rounded-lg object-contain"
                />
                <div className="text-center">
                  <span className="block text-sm text-emerald-400">
                    <FontAwesomeIcon icon={faCheck} className="mr-2" />
                    {formData.logoFile ? 'Novo logo selecionado' : 'Logo atual'}
                  </span>
                  <span className="text-xs text-slate-500">Clique para alterar</span>
                </div>
              </div>
            ) : (
              <>
                <FontAwesomeIcon icon={faUpload} className="h-6 w-6 text-slate-400" />
                <span className="text-sm text-slate-400">
                  Clique para enviar seu logo
                </span>
              </>
            )}
          </button>
        </div>

        {/* Primary Color */}
        <div>
          <label className="mb-2 block text-sm font-semibold text-slate-300">
            Cor primária da marca
          </label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={formData.primaryColor}
              onChange={(e) => setFormData(prev => ({ ...prev, primaryColor: e.target.value }))}
              className="h-12 w-12 cursor-pointer rounded-xl border border-slate-600 bg-transparent p-1"
            />
            <input
              type="text"
              value={formData.primaryColor}
              onChange={(e) => setFormData(prev => ({ ...prev, primaryColor: e.target.value }))}
              placeholder="#A020F0"
              className="flex-1 rounded-xl border border-slate-600 bg-slate-800 px-4 py-3 text-sm text-white outline-none transition focus:border-purple-500"
            />
          </div>
        </div>

        {/* Social Media */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-300">
                <FontAwesomeIcon icon={faInstagram as any} className="text-pink-500" />
                Instagram
              </label>
              <button
                type="button"
                onClick={() => onToggleFieldVisibility('instagram')}
                className={`flex h-7 w-7 items-center justify-center rounded-lg transition-all ${
                  (fieldVisibility.instagram ?? true)
                    ? 'text-purple-400 hover:bg-purple-500/20'
                    : 'text-slate-500 hover:bg-slate-700'
                }`}
                title={(fieldVisibility.instagram ?? true) ? 'Ocultar no canvas' : 'Mostrar no canvas'}
              >
                <FontAwesomeIcon 
                  icon={(fieldVisibility.instagram ?? true) ? faEye : faEyeSlash} 
                  className="h-3.5 w-3.5" 
                />
              </button>
            </div>
            <div className={`transition-opacity ${!(fieldVisibility.instagram ?? true) && 'opacity-50'}`}>
              <input
                type="text"
                value={formData.instagram}
                onChange={(e) => setFormData(prev => ({ ...prev, instagram: e.target.value }))}
                placeholder="@seuinstagram"
                className="w-full rounded-xl border border-slate-600 bg-slate-800 px-4 py-3 text-sm text-white outline-none transition focus:border-purple-500"
              />
            </div>
          </div>
          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-300">
                <FontAwesomeIcon icon={faFacebook as any} className="text-blue-500" />
                Facebook
              </label>
              <button
                type="button"
                onClick={() => onToggleFieldVisibility('facebook')}
                className={`flex h-7 w-7 items-center justify-center rounded-lg transition-all ${
                  (fieldVisibility.facebook ?? true)
                    ? 'text-purple-400 hover:bg-purple-500/20'
                    : 'text-slate-500 hover:bg-slate-700'
                }`}
                title={(fieldVisibility.facebook ?? true) ? 'Ocultar no canvas' : 'Mostrar no canvas'}
              >
                <FontAwesomeIcon 
                  icon={(fieldVisibility.facebook ?? true) ? faEye : faEyeSlash} 
                  className="h-3.5 w-3.5" 
                />
              </button>
            </div>
            <div className={`transition-opacity ${!(fieldVisibility.facebook ?? true) && 'opacity-50'}`}>
              <input
                type="text"
                value={formData.facebook}
                onChange={(e) => setFormData(prev => ({ ...prev, facebook: e.target.value }))}
                placeholder="/suapagina"
                className="w-full rounded-xl border border-slate-600 bg-slate-800 px-4 py-3 text-sm text-white outline-none transition focus:border-purple-500"
              />
            </div>
          </div>
        </div>

        {/* WhatsApp */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-300">
              <FontAwesomeIcon icon={faWhatsapp as any} className="text-green-500" />
              WhatsApp
            </label>
            <button
              type="button"
              onClick={() => onToggleFieldVisibility('whatsapp')}
              className={`flex h-7 w-7 items-center justify-center rounded-lg transition-all ${
                (fieldVisibility.whatsapp ?? true)
                  ? 'text-purple-400 hover:bg-purple-500/20'
                  : 'text-slate-500 hover:bg-slate-700'
              }`}
              title={(fieldVisibility.whatsapp ?? true) ? 'Ocultar no canvas' : 'Mostrar no canvas'}
            >
              <FontAwesomeIcon 
                icon={(fieldVisibility.whatsapp ?? true) ? faEye : faEyeSlash} 
                className="h-3.5 w-3.5" 
              />
            </button>
          </div>
          <div className={`transition-opacity ${!(fieldVisibility.whatsapp ?? true) && 'opacity-50'}`}>
            <input
              type="text"
              value={formData.whatsapp}
              onChange={(e) => setFormData(prev => ({ ...prev, whatsapp: e.target.value }))}
              placeholder="(11) 99999-9999"
              className="w-full rounded-xl border border-slate-600 bg-slate-800 px-4 py-3 text-sm text-white outline-none transition focus:border-purple-500"
            />
          </div>
        </div>

        {/* Website */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            <label className="block text-sm font-semibold text-slate-300">
              Website
            </label>
            <button
              type="button"
              onClick={() => onToggleFieldVisibility('website')}
              className={`flex h-7 w-7 items-center justify-center rounded-lg transition-all ${
                (fieldVisibility.website ?? true)
                  ? 'text-purple-400 hover:bg-purple-500/20'
                  : 'text-slate-500 hover:bg-slate-700'
              }`}
              title={(fieldVisibility.website ?? true) ? 'Ocultar no canvas' : 'Mostrar no canvas'}
            >
              <FontAwesomeIcon 
                icon={(fieldVisibility.website ?? true) ? faEye : faEyeSlash} 
                className="h-3.5 w-3.5" 
              />
            </button>
          </div>
          <div className={`transition-opacity ${!(fieldVisibility.website ?? true) && 'opacity-50'}`}>
            <input
              type="url"
              value={formData.website}
              onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
              placeholder="https://seusite.com.br"
              className="w-full rounded-xl border border-slate-600 bg-slate-800 px-4 py-3 text-sm text-white outline-none transition focus:border-purple-500"
            />
          </div>
        </div>

        {/* Address */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            <label className="block text-sm font-semibold text-slate-300">
              Endereço
            </label>
            <button
              type="button"
              onClick={() => onToggleFieldVisibility('address')}
              className={`flex h-7 w-7 items-center justify-center rounded-lg transition-all ${
                (fieldVisibility.address ?? true)
                  ? 'text-purple-400 hover:bg-purple-500/20'
                  : 'text-slate-500 hover:bg-slate-700'
              }`}
              title={(fieldVisibility.address ?? true) ? 'Ocultar no canvas' : 'Mostrar no canvas'}
            >
              <FontAwesomeIcon 
                icon={(fieldVisibility.address ?? true) ? faEye : faEyeSlash} 
                className="h-3.5 w-3.5" 
              />
            </button>
          </div>
          <div className={`transition-opacity ${!(fieldVisibility.address ?? true) && 'opacity-50'}`}>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
              placeholder="Rua Exemplo, 123 - Cidade/UF"
              className="w-full rounded-xl border border-slate-600 bg-slate-800 px-4 py-3 text-sm text-white outline-none transition focus:border-purple-500"
            />
          </div>
        </div>
      </div>

      {/* Editor Preferences Section */}
      <div className="mt-6 rounded-xl border border-slate-600 bg-slate-800/50 p-4">
        <div className="mb-3 flex items-center gap-2">
          <FontAwesomeIcon icon={faGear} className="text-purple-400" />
          <h3 className="text-sm font-semibold text-white">Predefinições do Editor</h3>
        </div>
        
        <p className="mb-4 text-xs text-slate-400">
          Configure como o editor aplica automaticamente suas informações de marca.
        </p>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          {/* Checkbox - Perguntar sempre */}
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={!preferences.skipPopup}
              onChange={(e) => onUpdatePreferences({ skipPopup: !e.target.checked })}
              disabled={isSavingPrefs}
              className="h-4 w-4 rounded border-slate-500 bg-slate-700 text-purple-500 focus:ring-purple-500"
            />
            <span className="text-sm text-slate-300">
              Perguntar sempre antes de editar
            </span>
          </label>

          {/* Reset button */}
          <button
            type="button"
            onClick={onResetPreferences}
            disabled={isSavingPrefs}
            className="flex items-center gap-2 rounded-lg border border-slate-600 bg-slate-700 px-3 py-1.5 text-xs font-medium text-slate-300 transition hover:bg-slate-600 hover:text-white disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faRotateLeft} className="text-xs" />
            Restaurar predefinições
          </button>
        </div>

        {preferences.skipPopup && (
          <p className="mt-3 text-xs text-amber-400/80">
            <FontAwesomeIcon icon={faGear} className="mr-1" />
            Popup desativado. O editor aplicará automaticamente todas as suas preferências de marca ao abrir um design.
          </p>
        )}
      </div>

      {/* Progress bar when uploading */}
      {isSubmitting && uploadProgress > 0 && (
        <div className="mt-4">
          <div className="h-2 w-full overflow-hidden rounded-full bg-slate-700">
            <div
              className="h-full rounded-full bg-gradient-to-r from-purple-500 to-emerald-400 transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <p className="mt-1 text-center text-xs text-slate-400">
            Enviando logo... {Math.round(uploadProgress)}%
          </p>
        </div>
      )}

      {/* Buttons */}
      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={onDismiss}
          disabled={isSubmitting}
          className="rounded-full border border-slate-600 bg-transparent px-5 py-2.5 text-sm font-medium text-slate-400 transition hover:bg-slate-800 hover:text-white disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={isSubmitting}
          className="flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-purple-600 to-emerald-500 px-6 py-2.5 text-sm font-semibold text-white shadow-lg transition hover:shadow-xl hover:brightness-110 disabled:opacity-50"
        >
          {isSubmitting ? (
            <>
              <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" />
              Salvando...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faCheck} className="h-4 w-4" />
              Salvar configurações
            </>
          )}
        </button>
      </div>
    </div>
  );
}
